import React, { useState, useRef, useEffect } from "react";
import { Clickable } from "@/components/toolkit/Clickable";
import "./s.accordion.css";


const HEADER_SIZE = 40;

export interface AccordionItem {
    header: string;
    headerSlot?: React.ReactNode;
    content: React.ReactNode;
    controls?: React.ReactNode;
    headerClassName?: string;
}

interface AccordionProps {
    items: AccordionItem[];
    defaultOpenIndex?: number;
    openIndex?: number;
    onOpenChange?: (index: number) => void;
    direction?: "vertical" | "horizontal";
}

export function Accordion({ items, defaultOpenIndex = 0, openIndex: controlledIndex, onOpenChange, direction = "vertical" }: AccordionProps) {
    const [internalIndex, setInternalIndex] = useState(defaultOpenIndex);
    const openIndex = controlledIndex !== undefined ? controlledIndex : internalIndex;

    const [animating, setAnimating] = useState(false);
    const animTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleTabClick = (i: number) => {
        if (controlledIndex === undefined) setInternalIndex(i);
        onOpenChange?.(i);
        setAnimating(true);
        if (animTimerRef.current) clearTimeout(animTimerRef.current);
        animTimerRef.current = setTimeout(() => setAnimating(false), 90);
    };

    const accordionRef = useRef<HTMLDivElement>(null);
    const [contentSize, setContentSize] = useState(0);

    useEffect(() => {
        const el = accordionRef.current;
        if (!el) return;
        const update = () => {
            const total = direction === "horizontal" ? el.clientWidth : el.clientHeight;
            setContentSize(Math.max(0, total - items.length * HEADER_SIZE));
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, [items.length, direction]);

    useEffect(() => () => {
        if (animTimerRef.current) clearTimeout(animTimerRef.current);
    }, []);

    const cls = "accordion"
        + (direction === "horizontal" ? " accordion--horizontal" : "")
        + (animating ? " accordion--animating" : "");

    return (
        <div ref={accordionRef} className={cls}>
            {items.map((item, i) => {
                const isOpen = i === openIndex;
                const contentStyle = direction === "horizontal"
                    ? { width: isOpen ? contentSize : 0 }
                    : { height: isOpen ? contentSize : 0 };
                return (
                    <div key={i} className={"accordion__section" + (isOpen ? " active" : "")}>
                        <Clickable captureTouch onClick={() => handleTabClick(i)}>
                            <button className={"accordion__header" + (item.headerClassName ? " " + item.headerClassName : "")}>
                                <span className="accordion__chevron">›</span>
                                {item.header}
                                {item.headerSlot && (
                                    <span className="accordion__header_slot" onClick={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}>
                                        {item.headerSlot}
                                    </span>
                                )}
                                {item.controls && (
                                    <span className="accordion__controls" onClick={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}>
                                        {item.controls}
                                    </span>
                                )}
                            </button>
                        </Clickable>
                        <div className="accordion__content" style={contentStyle}>
                            {item.content}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
